import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  ArrowUpRight,
  Search,
  KeyRound,
  ImageIcon,
  Star,
  TrendingUp,
  Globe,
  CheckCircle2,
  Target,
  Sparkles,
  LineChart,
  Repeat,
} from "lucide-react";
import Navbar from "../components/homepage/Navbar";
import Footer from "../components/homepage/Footer";
import CTABanner from "../components/homepage/CTABanner";
import FAQSection from "../components/homepage/FAQSection";
import Stats from "../components/homepage/Stats";

const PILLARS = [
  {
    icon: KeyRound,
    title: "Keyword Research",
    text: "Deep semantic analysis across iOS App Store and Google Play. Long-tail clusters, competitor gaps and search-volume mapping for every market.",
  },
  {
    icon: Target,
    title: "Listing Optimization",
    text: "Title, subtitle, short & full description, promo text — rewritten for the algorithm and the human reading on a 6-inch screen.",
  },
  {
    icon: ImageIcon,
    title: "Screenshots & Video",
    text: "A/B tested creative that lifts conversion 30–80%. Localized for every market we ship to.",
  },
  {
    icon: Star,
    title: "Ratings & Reviews",
    text: "Smart prompts, reply ops and review-based ASO insights. We move 3.x apps to 4.5+ in 60 days.",
  },
  {
    icon: LineChart,
    title: "Tracking & Reporting",
    text: "Live keyword ranks, install velocity, conversion rate and impression share — weekly delivered, no black box.",
  },
  {
    icon: Globe,
    title: "Multi-Market Localization",
    text: "Localized ASO for USA, UK, AU, UAE, CA, DE, JP, SG, BR, IN — same app, 15+ rankings.",
  },
];

const PROCESS = [
  { n: "01", title: "Audit", text: "12-page audit of your listing, keywords, competitors and conversion funnel." },
  { n: "02", title: "Strategy", text: "Custom keyword stack, listing brief and screenshot direction per market." },
  { n: "03", title: "Optimize", text: "We rebuild metadata, screenshots and video. Ratings ops kick in." },
  { n: "04", title: "Test", text: "A/B tests on Play Console + iOS Custom Product Pages run continuously." },
  { n: "05", title: "Scale", text: "Winners go live in all markets. Paid UA stacks on top of ranked organic." },
];

const RESULTS = [
  { metric: "+412%", label: "organic installs in 90 days", market: "UAE FinTech, Google Play" },
  { metric: "#2", label: "ranked on US Top Casual chart", market: "Gaming, App Store" },
  { metric: "4.8★", label: "from 4.2 in 60 days", market: "Health, Global iOS + Android" },
  { metric: "+267%", label: "lift in conversion rate", market: "EdTech, Apple Search Ads" },
];

const DELIVERABLES = [
  "Full keyword research (iOS + Android, primary market)",
  "Title, subtitle and description rewrite",
  "Promo text & in-app event copy (iOS)",
  "5–8 A/B-tested screenshots per platform",
  "Localization for up to 5 markets",
  "Ratings & reviews ops setup",
  "Weekly performance memo + Slack channel",
  "Quarterly strategy review",
];

const ASO_FAQ_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: [
    {
      "@type": "Question",
      name: "What is App Store Optimization (ASO)?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "App Store Optimization (ASO) is the practice of improving an app's discoverability and conversion in the Apple App Store and Google Play Store through keyword optimization, listing copy, screenshots, ratings management and continuous A/B testing.",
      },
    },
    {
      "@type": "Question",
      name: "How is ASO different between iOS and Android?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Apple App Store relies heavily on the title, subtitle and 100-character keyword field, while Google Play indexes the full long description. Screenshots and video also weight differently, which is why platform-specific ASO strategy matters.",
      },
    },
    {
      "@type": "Question",
      name: "How much does Lumotis's ASO service cost?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Lumotis ASO plans start at $899/month (Starter), $2,499/month (Growth), and Custom for Enterprise multi-market engagements.",
      },
    },
    {
      "@type": "Question",
      name: "Do you offer ASO for both Apple App Store and Google Play Store?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Yes. Lumotis provides full-stack ASO for both Apple App Store (iOS) and Google Play Store (Android), with platform-specific strategies, separate keyword research and store-specific creative.",
      },
    },
  ],
};

export default function AsoPage() {
  return (
    <>
      <Helmet>
        <title>
          App Store Optimization (ASO) Services — Lumotis India's #1 ASO Agency
        </title>
        <meta
          name="description"
          content="Lumotis's App Store Optimization (ASO) services help iOS and Android apps rank #1 on Apple App Store & Google Play Store. Keyword research, listing optimization, A/B tested screenshots, ratings management. Free ASO audit available."
        />
        <meta
          name="keywords"
          content="app store optimization, ASO services, ASO agency, iOS ASO, Android ASO, Google Play optimization, Apple App Store optimization, app keyword research, ASO India, app marketing agency, mobile app marketing, app ranking services"
        />
        <link rel="canonical" href="https://lumotis.com/aso" />
        <meta name="robots" content="index, follow" />
        <meta
          property="og:title"
          content="App Store Optimization Services — Lumotis"
        />
        <meta
          property="og:description"
          content="Rank #1 on App Store & Google Play. ASO keyword research, listing optimization, screenshots & ratings — by India's #1 ASO agency."
        />
        <meta property="og:url" content="https://lumotis.com/aso" />
        <meta property="og:type" content="website" />
        <script type="application/ld+json">
          {JSON.stringify(ASO_FAQ_SCHEMA)}
        </script>
      </Helmet>
      <Navbar />

      <main data-testid="aso-page">
        {/* HERO */}
        <section
          className="relative pt-28 md:pt-32 pb-20 md:pb-24 bg-white overflow-hidden"
          data-testid="aso-hero"
        >
          <div className="absolute inset-0 bg-grid-pattern opacity-70 pointer-events-none" />
          <div className="absolute right-0 top-0 w-2/3 h-2/3 bg-gradient-to-bl from-electric/10 via-transparent to-transparent pointer-events-none" />

          <div className="container relative grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
              <nav
                aria-label="Breadcrumb"
                className="flex items-center gap-2 text-xs uppercase tracking-widest text-zinc-500 mb-6"
              >
                <Link to="/" className="hover:text-electric">
                  Home
                </Link>
                <span>/</span>
                <span className="text-charcoal">ASO Services</span>
              </nav>
              <p className="text-overline text-electric mb-4">
                App Store Optimization
              </p>
              <motion.h1
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="font-display text-[2.6rem] sm:text-5xl lg:text-[4.25rem] leading-[1.02] font-extrabold text-charcoal text-balance"
                data-testid="aso-headline"
              >
                The{" "}
                <span className="relative inline-block">
                  <span className="relative z-10">App Store Optimization</span>
                  <span className="absolute inset-x-0 bottom-1 h-3 bg-electric/30 -z-0" />
                </span>{" "}
                engine that ranks apps #1 on iOS & Google Play.
              </motion.h1>
              <p className="mt-6 text-lg md:text-xl text-zinc-600 max-w-2xl leading-relaxed">
                Keyword research, listing optimization, A/B-tested screenshots
                and ratings management — purpose-built for Apple App Store and
                Google Play Store. Trusted by 100+ app founders across 15+
                markets.
              </p>
              <div className="mt-8 flex flex-wrap items-center gap-3">
                <a
                  href="#audit"
                  data-testid="aso-cta-audit"
                  className="inline-flex items-center gap-2 bg-electric text-white px-6 py-3.5 font-semibold hover:bg-electric-400 hover:-translate-y-0.5 transition-all shadow-lg rounded-xl"
                >
                  Get Free ASO Audit <ArrowRight className="w-4 h-4" />
                </a>
                <Link
                  to="/#case-studies"
                  data-testid="aso-cta-cases"
                  className="inline-flex items-center gap-2 bg-white border border-charcoal text-charcoal px-6 py-3.5 font-semibold hover:bg-charcoal hover:text-white transition-colors"
                >
                  See Case Studies <ArrowUpRight className="w-4 h-4" />
                </Link>
              </div>
            </div>

            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.7 }}
              className="lg:col-span-5 relative"
            >
              <div className="relative bg-charcoal text-white p-8 md:p-10 shadow-2xl shadow-electric/20 rounded-2xl">
                <div className="flex items-center justify-between mb-6">
                  <span className="text-overline text-electric">
                    Live Keyword Rank
                  </span>
                  <span className="w-2 h-2 bg-electric animate-blink" />
                </div>
                <div className="space-y-4">
                  {[
                    { kw: "meal delivery near me", before: 38, after: 2 },
                    { kw: "best food app", before: 71, after: 6 },
                    { kw: "order food online", before: 24, after: 1 },
                  ].map((row) => (
                    <div
                      key={row.kw}
                      className="flex items-center justify-between border-b border-white/10 pb-3"
                    >
                      <div>
                        <div className="font-display font-bold text-base">
                          {row.kw}
                        </div>
                        <div className="text-[10px] uppercase tracking-widest text-zinc-500 mt-0.5">
                          USA · App Store
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-zinc-500 line-through text-sm">
                          #{row.before}
                        </span>
                        <ArrowRight className="w-4 h-4 text-electric" />
                        <span className="font-display font-extrabold text-2xl text-electric">
                          #{row.after}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-6 flex items-center gap-2 text-xs text-zinc-400">
                  <TrendingUp className="w-3.5 h-3.5 text-electric" />
                  Avg. lift after 12 weeks of Lumotis ASO
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* PILLARS */}
        <section
          className="py-20 md:py-28 bg-zinc-50 border-y border-zinc-200"
          data-testid="aso-pillars"
        >
          <div className="container">
            <div className="grid lg:grid-cols-12 gap-10 items-end mb-12">
              <div className="lg:col-span-7">
                <p className="text-overline text-electric mb-3">
                  01 — What's inside
                </p>
                <h2 className="font-display text-4xl md:text-5xl font-extrabold text-charcoal text-balance">
                  Six pillars of App Store Optimization, executed under one roof.
                </h2>
              </div>
              <p className="lg:col-span-5 text-zinc-600 text-lg leading-relaxed">
                ASO isn't one thing — it's six. Most agencies do two of them.
                Lumotis runs the full stack so nothing leaks.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-zinc-200 border border-zinc-200">
              {PILLARS.map((p, idx) => {
                const Icon = p.icon;
                return (
                  <motion.div
                    key={p.title}
                    initial={{ opacity: 0, y: 12 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: idx * 0.04 }}
                    className="group bg-white p-7 md:p-9 hover:bg-charcoal hover:text-white transition-colors"
                    data-testid={`pillar-${idx}`}
                  >
                    <div className="flex items-center justify-between mb-6">
                      <span className="w-11 h-11 grid place-items-center bg-electric text-white">
                        <Icon className="w-5 h-5" strokeWidth={2.2} />
                      </span>
                      <span className="text-overline text-zinc-500 group-hover:text-zinc-300">
                        0{idx + 1}
                      </span>
                    </div>
                    <h3 className="font-display text-xl md:text-2xl font-bold leading-tight">
                      {p.title}
                    </h3>
                    <p className="mt-3 text-[15px] text-zinc-600 group-hover:text-zinc-300 leading-relaxed">
                      {p.text}
                    </p>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>

        {/* RESULTS STRIP */}
        <section
          className="py-20 md:py-24 bg-white"
          data-testid="aso-results"
        >
          <div className="container">
            <div className="max-w-3xl mb-12">
              <p className="text-overline text-electric mb-3">02 — Results</p>
              <h2 className="font-display text-4xl md:text-5xl font-extrabold text-charcoal text-balance">
                Real ASO wins, audited and documented.
              </h2>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 divide-x divide-zinc-200 border-y border-zinc-200">
              {RESULTS.map((r, idx) => (
                <motion.div
                  key={r.label}
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: idx * 0.05 }}
                  className="px-6 py-10"
                  data-testid={`aso-result-${idx}`}
                >
                  <div className="font-display text-5xl md:text-6xl font-black text-electric leading-none">
                    {r.metric}
                  </div>
                  <p className="mt-3 text-sm text-charcoal leading-snug font-medium">
                    {r.label}
                  </p>
                  <p className="mt-2 text-xs uppercase tracking-widest text-zinc-500">
                    {r.market}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* PROCESS */}
        <section
          className="py-20 md:py-28 bg-charcoal text-white relative overflow-hidden"
          data-testid="aso-process"
        >
          <div className="absolute inset-0 bg-grid-pattern-dark opacity-50 pointer-events-none" />
          <div className="container relative">
            <div className="max-w-3xl mb-14">
              <p className="text-overline text-electric mb-3">03 — Process</p>
              <h2 className="font-display text-4xl md:text-5xl font-extrabold text-balance">
                Our ASO sprint — 90 days from audit to page-one ranks.
              </h2>
            </div>
            <ol className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-px bg-charcoal-700 border border-charcoal-700">
              {PROCESS.map((s) => (
                <li
                  key={s.n}
                  className="bg-charcoal-900 p-7 md:p-8"
                  data-testid={`aso-process-${s.n}`}
                >
                  <span className="font-display text-5xl font-black text-electric/30 leading-none block mb-6">
                    {s.n}
                  </span>
                  <h3 className="font-display text-xl font-bold">{s.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-zinc-400">
                    {s.text}
                  </p>
                </li>
              ))}
            </ol>
          </div>
        </section>

        {/* DELIVERABLES + WHY ASO */}
        <section
          className="py-20 md:py-28 bg-zinc-50 border-y border-zinc-200"
          data-testid="aso-deliverables"
        >
          <div className="container grid lg:grid-cols-12 gap-12">
            <div className="lg:col-span-7">
              <p className="text-overline text-electric mb-3">
                04 — Deliverables
              </p>
              <h2 className="font-display text-4xl md:text-5xl font-extrabold text-charcoal text-balance mb-8">
                What you get every month.
              </h2>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
                {DELIVERABLES.map((d) => (
                  <li
                    key={d}
                    className="flex items-start gap-3 text-[15px] text-zinc-700"
                  >
                    <CheckCircle2
                      className="w-5 h-5 mt-0.5 text-electric flex-shrink-0"
                      strokeWidth={2.2}
                    />
                    <span>{d}</span>
                  </li>
                ))}
              </ul>
              <Link
                to="/#pricing"
                data-testid="aso-pricing-link"
                className="mt-10 inline-flex items-center gap-2 bg-charcoal text-white px-5 py-3 font-semibold hover:bg-electric transition-colors"
              >
                See ASO pricing <ArrowRight className="w-4 h-4" />
              </Link>
            </div>

            <aside className="lg:col-span-5 bg-white border border-zinc-200 p-8 md:p-10 self-start">
              <div className="flex items-center gap-2 mb-4">
                <Sparkles className="w-5 h-5 text-electric" />
                <span className="text-overline text-zinc-500">
                  Why ASO matters
                </span>
              </div>
              <p className="font-display text-2xl md:text-3xl font-bold text-charcoal leading-snug">
                65%+ of all app downloads come from search inside the App Store
                & Google Play.
              </p>
              <p className="mt-4 text-zinc-600 leading-relaxed">
                Without ASO, you're paying for installs you could be earning.
                Done right, App Store Optimization compounds — every keyword
                you rank for keeps delivering installs at zero marginal cost.
              </p>
              <div className="mt-6 flex items-center gap-3 text-sm font-semibold text-charcoal">
                <Repeat className="w-4 h-4 text-electric" /> Compound, organic,
                permanent.
              </div>
            </aside>
          </div>
        </section>

        <Stats />
        <CTABanner />
        <FAQSection />
      </main>

      <Footer />
    </>
  );
}
